import { useCallback, useMemo, useReducer, useRef } from 'react';
import { Tabs } from '@packages/ui/composites';
import { Section } from '@packages/ui/layout';
import type { ResolvedTabBlock, RenderBlockFn, BlockRendererSharedProps } from '../types/blocks';
import { useTabEventAdapter } from '@packages/block-events/react/adapters/useTabEventAdapter';

export interface TabBlockRendererProps extends BlockRendererSharedProps {
  block: ResolvedTabBlock;
  renderBlock: RenderBlockFn;
}

interface TabState {
  activeId: string;
  visited: Set<string>;
  badges: Record<string, string | number | null>;
  /** How many times each tab has been activated — used as remount key. */
  switchCounts: Record<string, number>;
}

type TabAction =
  | { type: 'SWITCH'; id: string }
  | { type: 'SET_BADGE'; tabId: string; value: string | number | null }
  | { type: 'SET_ACTIVE_TAB'; tabId: string };

function tabReducer(state: TabState, action: TabAction): TabState {
  switch (action.type) {
    case 'SWITCH':
    case 'SET_ACTIVE_TAB': {
      const id = action.type === 'SWITCH' ? action.id : action.tabId;
      const nextVisited = state.visited.has(id)
        ? state.visited
        : new Set([...state.visited, id]);
      const nextSwitchCounts = {
        ...state.switchCounts,
        [id]: (state.switchCounts[id] ?? 0) + 1,
      };
      return { ...state, activeId: id, visited: nextVisited, switchCounts: nextSwitchCounts };
    }
    case 'SET_BADGE':
      return { ...state, badges: { ...state.badges, [action.tabId]: action.value } };
    default:
      return state;
  }
}

export function TabBlockRenderer({
  block,
  renderBlock,
  params,
  meta,
  onSuccess,
  onError,
  onCancel,
  inheritedDataSource,
  handle,
}: TabBlockRendererProps): JSX.Element {
  const defaultTab = block.defaultTab ?? block.tabs[0]?.id ?? '';
  const shared: BlockRendererSharedProps = {
    params,
    meta,
    onSuccess,
    onError,
    onCancel,
    inheritedDataSource,
    handle,
  };

  const [state, dispatch] = useReducer(tabReducer, {
    activeId: defaultTab,
    visited: new Set<string>([defaultTab]),
    badges: {},
    switchCounts: { [defaultTab]: 1 },
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const adapterOptions = useMemo(() => ({
    setBadge: (tabId: string, value: string | number | null) =>
      dispatch({ type: 'SET_BADGE', tabId, value }),
    setActiveTab: (tabId: string) =>
      dispatch({ type: 'SET_ACTIVE_TAB', tabId }),
    getActiveTab: () => stateRef.current.activeId,
  }), []);

  const { publishTabChanged } = useTabEventAdapter(block.semanticId, adapterOptions);

  const handleTabSwitch = useCallback(
    (id: string) => {
      const prev = stateRef.current.activeId;
      dispatch({ type: 'SWITCH', id });
      publishTabChanged(id, prev);
    },
    [publishTabChanged],
  );

  return (
    <Section title={block.title} semanticId={block.semanticId}>
      <Tabs.Root
        value={state.activeId}
        semanticId={block.semanticId ?? 'tabBlock'}
        centered={block.tabsAlign === 'center'}
        onValueChange={handleTabSwitch}
      >
        <Tabs.List>
          {block.tabs.map((tab) => {
            const badge = state.badges[tab.id];
            const hasBadge = badge != null && badge !== '';
            if (!hasBadge) {
              return (
                <Tabs.Trigger key={tab.id} value={tab.id}>
                  {tab.label}
                </Tabs.Trigger>
              );
            }
            return (
              <Tabs.Trigger key={tab.id} value={tab.id}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {tab.label}
                  <span style={{ marginLeft: '6px', fontSize: '0.75em', opacity: 0.7 }}>({badge})</span>
                </span>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>
      </Tabs.Root>

      {block.tabs.map((tab) => {
        const isActive = state.activeId === tab.id;
        if (tab.remountOnSwitch) {
          // Unmount when hidden — fresh mount on every visit. Key changes on each activation.
          if (!isActive) return null;
          return (
            <div key={`${tab.id}-${state.switchCounts[tab.id] ?? 1}`}>
              {renderBlock(tab.block, shared)}
            </div>
          );
        }
        if (!state.visited.has(tab.id)) return null;
        return (
          <div key={tab.id} style={{ display: isActive ? 'block' : 'none' }}>
            {renderBlock(tab.block, shared)}
          </div>
        );
      })}
    </Section>
  );
}
